/**
 * Traditional Chinese (zh-TW) Translations
 * 繁體中文翻譯
 */

import type { PricingMessages } from './types';

const zhTW: PricingMessages = {
    pageTitle: '產品定價',

    hero: {
        title: '選擇適合您的方案',
        subtitle: '靈活的定價，強大的功能',
        billingToggle: {
            monthly: '按月付費',
            annual: '按年付費',
            saveLabel: '節省 {percent}%',
        },
    },

    plans: {
        lite: {
            name: '輕量版',
            description: '每月一次體驗所有功能',
            cta: '立即購買',
            priceLabel: '{amount} / 月',
        },
        pro: {
            name: '專業版',
            description: '專業從業者的無限撰寫',
            cta: '立即購買',
            priceLabel: '{amount} / 月',
            features: {
                unlimited_drafts: '專業版文件額度',
                priority_support: '優先支援',
            },
        },
        max: {
            name: 'Max',
            description: '高頻專利撰寫的不限量文件建立',
            cta: '立即購買',
            priceLabel: '{amount} / 月',
            features: {
                unlimited_documents: '文件建立數量不限',
            },
        },
        enterprise: {
            name: '企業版',
            description: '團隊和企業的定制解決方案',
            cta: '聯繫我們',
            priceLabel: '自定義價格',
        },
    },

    features: {
        drafting: {
            title: '專利撰寫',
            items: {
                specification_generation: '說明書生成',
                claims_generation: '權利要求書生成',
                abstract_generation: '摘要生成',
                embodiment_expansion: '實施例擴展',
                figure_handling: '附圖處理',
                drafts_per_month: '每月撰寫數量',
            },
        },
        export: {
            title: '導出與格式',
            items: {
                export_word: 'Word 導出',
                export_pdf: 'PDF 導出',
                multi_jurisdiction: '多司法管轄區格式',
                custom_templates: '自定義模板',
            },
        },
        collaboration: {
            title: '協作',
            items: {
                workspace_share: '分享',
                workspace_comment: '評論',
                workspace_edit: '協作編輯',
                version_history: '版本歷史',
            },
        },
        administration: {
            title: '管理',
            items: {
                admin_backend: '管理後台',
                user_management: '用戶管理',
                usage_analytics: '使用分析',
            },
        },
        security: {
            title: '安全',
            items: {
                data_encryption: '數據加密',
                sso: '單點登錄(SSO)',
                audit_logs: '審計日誌',
            },
        },
        support: {
            title: '支持',
            items: {
                support_model: '支持模式',
                training: '培訓與入門指導',
            },
        },
        values: {
            unlimited: '無限',
            included: '包含',
            notIncluded: '不包含',
            custom: '自定義',
            allJurisdictions: '所有專利局',
        },
    },

    faq: {
        title: '常見問題',
        items: [
            {
                question: '生成的專利質量如何？',
                answer:
          '我們的AI系統經過大量高質量專利數據訓練，能夠生成符合專業標準的專利文檔。所有生成的內容都經過精心設計，確保邏輯清晰、表述準確。',
            },
            {
                question: '支持哪些專利局格式？',
                answer:
          '我們支持包括中國國家知識產權局(CNIPA)、美國專利商標局(USPTO)、歐洲專利局(EPO)、日本國特許廳(JPO)在內的主要專利局格式，以及其他多個國家和地區的專利局格式。',
            },
            {
                question: '數據安全如何保障？',
                answer:
          '我們採用銀行級加密技術保護您的數據，所有數據傳輸均使用SSL加密。我們嚴格遵守數據保護法規，不會與第三方共享您的專利內容。',
            },
            {
                question: '是否需要專業技術背景？',
                answer:
          '不需要。我們的系統設計簡單直觀，任何人都可以輕鬆上手。對於專業用戶，我們也提供高級功能和自定義選項。',
            },
            {
                question: '可以定制化嗎？',
                answer:
          '可以。企業版提供完整的定制化服務，包括專屬模板、工作流程定制、API集成等。我們的團隊會根據您的具體需求提供解決方案。',
            },
            {
                question: '價格如何計算？',
                answer:
          '輕量版按每月9.9美元計費，每月可撰寫1篇專利。專業版提供月付和年付兩種選擇，年付可享受20%折扣。企業版價格根據團隊規模和需求定制。',
            },
            {
                question: '支持團隊協作嗎？',
                answer:
          '專業版和企業版都支持團隊協作功能，包括文檔分享、評論、版本歷史等。企業版還提供更高級的協作編輯和用戶管理功能。',
            },
            {
                question: '如果不滿意可以退款嗎？',
                answer:
          '我們提供7天無理由退款保證。如果您對服務不滿意，可以在購買後7天內申請全額退款。',
            },
        ],
    },

    cta: {
        title: '準備好開始了嗎？',
        subtitle: '選擇適合您的方案，立即開始您的專利撰寫之旅',
        buttonText: '立即開始',
        selectPlan: '選擇方案',
        contactSales: '聯繫銷售',
        learnMore: '了解更多',
        getStarted: '立即開始',
        emailSubject: 'Eureka Drafting - 企業版諮詢',
        planSelected: '已選擇 {plan} 方案',
        getStartedMessage: '即將跳轉至註冊頁面...',
    },

    errors: {
        loadPlansFailed: '加載定價信息失敗，請稍後重試',
        invalidPlan: '無效的定價計劃',
        networkError: '網絡連接失敗',
    },

    common: {
        perMonth: '/ 月',
        perYear: '/ 年',
        billedMonthly: '按月計費',
        billedAnnually: '按年計費',
        popular: '熱門',
        recommended: '推薦',
        comingSoon: '即將推出',
    },

    payment: {
        verifying: '正在驗證支付...',
        pleaseWait: '請稍候，我們正在確認您的支付',
        backToPricing: '返回定價頁',
        tryAgain: '重試',

        success: {
            title: '支付成功！',
            message: '感謝您的訂閱，您的賬戶已激活。',
        },

        failed: {
            title: '支付失敗',
            message: '我們無法處理您的支付，請重試。',
        },

        canceled: {
            title: '支付已取消',
            message: '您取消了支付，沒有產生任何費用。',
        },

        errors: {
            createSessionFailed: '創建支付會話失敗，請重試。',
            verifyFailed: '驗證支付狀態失敗',
            verificationFailed: '支付驗證失敗',
            verificationError: '驗證過程中發生錯誤',
            invalidCallback: '無效的支付回調',
            noPriceId: '價格信息未配置',
        },
    },
};

export default zhTW;
